import type { AssistantQuestionAnswerMap } from "./askUser/types";
import type { SubmitContextHandoff } from "./threads/types";

export const CLAUDIA_INPUT_TOO_LARGE_CODE = "CLAUDIA_INPUT_TOO_LARGE";

export const CLAUDIA_INPUT_LIMITS = {
  promptChars: 32_000,
  askUserCustomAnswerChars: 4_000,
  steeringChars: 8_000,
  viewItemsPerList: 200,
  handoffJsonBytes: 64_000,
} as const;

export const CLAUDIA_ATTACHMENT_TOKEN_ESTIMATES = {
  imagePart: 1_600,
  documentPart: 5_000,
  spreadsheetPart: 3_000,
  textBytesPerToken: 4,
  documentBytesPerToken: 750,
} as const;

export class ClaudiaInputTooLargeError extends Error {
  readonly code = CLAUDIA_INPUT_TOO_LARGE_CODE;
  readonly label: string;
  readonly actual: number;
  readonly limit: number;

  constructor(label: string, actual: number, limit: number) {
    super(`${CLAUDIA_INPUT_TOO_LARGE_CODE}: ${label} is too large (${actual}/${limit}).`);
    this.name = "ClaudiaInputTooLargeError";
    this.label = label;
    this.actual = actual;
    this.limit = limit;
  }
}

export function assertWithinLimit(actual: number, limit: number, label: string): void {
  if (actual > limit) {
    throw new ClaudiaInputTooLargeError(label, actual, limit);
  }
}

function serializedJsonLength(value: unknown) {
  return JSON.stringify(value).length;
}

function assertViewItemListWithinLimit(
  value: unknown,
  label: string,
  limit = CLAUDIA_INPUT_LIMITS.viewItemsPerList
) {
  if (Array.isArray(value)) {
    assertWithinLimit(value.length, limit, label);
  }
}

export function assertContextHandoffWithinLimits(handoff: SubmitContextHandoff | undefined): void {
  if (!handoff) {
    return;
  }

  assertWithinLimit(
    serializedJsonLength(handoff),
    CLAUDIA_INPUT_LIMITS.handoffJsonBytes,
    "Context handoff"
  );

  const context = handoff.context as Record<string, unknown>;
  assertViewItemListWithinLimit(context.visibleItems, "Context visible item list");
  assertViewItemListWithinLimit(context.hiddenMatchingItems, "Context hidden item list");
  assertViewItemListWithinLimit(context.allScopedItems, "Context scoped item list");
}

export function assertAskUserAnswersWithinLimits(answers: AssistantQuestionAnswerMap): void {
  for (const answer of Object.values(answers)) {
    if (answer.kind !== "custom") {
      continue;
    }
    assertWithinLimit(
      answer.text.length,
      CLAUDIA_INPUT_LIMITS.askUserCustomAnswerChars,
      "Custom ask_user answer"
    );
  }
}
